<template>
  <div class="voice-player" :class="{ 'voice-player--own': isMe }">
    <!-- Sender Avatar (for received messages) -->
    <div v-if="!isMe && senderPhotoUrl" class="voice-player__avatar">
      <img :src="senderPhotoUrl" alt="" @error="onAvatarError" />
    </div>
    <div v-else-if="!isMe" class="voice-player__avatar voice-player__avatar--placeholder">
      <i class="bi bi-person-fill"></i>
    </div>

    <!-- Play/Pause Button -->
    <button 
      class="voice-player__play-btn" 
      @click="togglePlayPause"
      :disabled="!audioUrl || isLoading"
    >
      <i v-if="isLoading" class="bi bi-hourglass-split voice-player__loading"></i>
      <i v-else-if="isPlaying" class="bi bi-pause-fill"></i>
      <i v-else class="bi bi-play-fill"></i>
    </button>

    <!-- Waveform Container -->
    <div class="voice-player__waveform-container" @click="seekToPosition">
      <canvas 
        ref="waveformCanvas" 
        class="voice-player__waveform"
        :width="canvasWidth"
        :height="canvasHeight"
      ></canvas>
      <!-- Seek thumb indicator -->
      <div 
        class="voice-player__thumb" 
        :style="{ left: progressPercent + '%' }"
      ></div>
    </div>

    <!-- Duration / Current Time -->
    <div class="voice-player__time">
      {{ formattedTime }}
    </div>

    <!-- Playback Speed Button -->
    <button 
      class="voice-player__speed-btn" 
      @click="cyclePlaybackSpeed"
      :title="playbackSpeed + 'x'"
    >
      {{ playbackSpeed }}x
    </button>

    <!-- Trailing slot (for time/read status) -->
    <slot name="trailing"></slot>
  </div>
</template>

<script>
export default {
  name: 'VoiceMessagePlayer',
  props: {
    audioUrl: {
      type: String,
      required: true,
    },
    voiceDuration: {
      type: Number,
      default: 0,
    },
    isMe: {
      type: Boolean,
      default: false,
    },
    senderPhotoUrl: {
      type: String,
      default: null,
    },
    waveformData: {
      type: Array,
      default: null,
    },
    autoPlay: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['playback-complete'],
  data() {
    return {
      audio: null,
      isPlaying: false,
      isLoading: false,
      currentTime: 0,
      duration: 0,
      playbackSpeed: 1.0,
      playbackSpeeds: [1.0, 1.5, 2.0],
      waveHeights: [],
      canvasWidth: 160,
      canvasHeight: 32,
      barCount: 40,
    };
  },
  computed: {
    progressPercent() {
      if (!this.duration || this.duration === 0) return 0;
      return Math.min(100, (this.currentTime / this.duration) * 100);
    },
    formattedTime() {
      const time = this.isPlaying || this.currentTime > 0 
        ? this.currentTime 
        : (this.voiceDuration || this.duration || 0);
      return this.formatDuration(Math.round(time));
    },
  },
  watch: {
    audioUrl: {
      immediate: true,
      handler(newUrl) {
        if (newUrl) {
          this.initAudio();
        }
      },
    },
    waveformData: {
      immediate: true,
      handler() {
        this.generateWaveform();
        this.$nextTick(() => this.drawWaveform());
      },
    },
  },
  mounted() {
    this.generateWaveform();
    this.$nextTick(() => this.drawWaveform());
    
    if (this.autoPlay && this.audioUrl) {
      this.$nextTick(() => this.play());
    }
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    initAudio() {
      this.cleanup();
      
      if (!this.audioUrl) return;
      
      this.audio = new Audio(this.audioUrl);
      this.audio.preload = 'metadata';
      
      this.audio.addEventListener('loadedmetadata', () => {
        if (!this.audio) return;
        this.duration = this.audio.duration || this.voiceDuration || 0;
        this.isLoading = false;
      });
      
      this.audio.addEventListener('timeupdate', () => {
        if (!this.audio) return;
        this.currentTime = this.audio.currentTime;
        this.drawWaveform();
      });
      
      this.audio.addEventListener('ended', () => {
        this.isPlaying = false;
        this.currentTime = 0;
        this.drawWaveform();
        this.$emit('playback-complete');
      });
      
      this.audio.addEventListener('play', () => {
        this.isPlaying = true;
      });
      
      this.audio.addEventListener('pause', () => {
        this.isPlaying = false;
      });
      
      this.audio.addEventListener('error', (e) => {
        console.error('[VoiceMessagePlayer] Audio error:', e);
        this.isLoading = false;
        this.isPlaying = false;
      });
      
      this.audio.addEventListener('waiting', () => {
        this.isLoading = true;
      });
      
      this.audio.addEventListener('canplay', () => {
        this.isLoading = false;
      });
    },
    
    cleanup() {
      if (this.audio) {
        this.audio.pause();
        this.audio.src = '';
        this.audio = null;
      }
      this.isPlaying = false;
      this.currentTime = 0;
    },
    
    generateWaveform() {
      if (this.waveformData && this.waveformData.length > 0) {
        this.waveHeights = this.normalizeWaveform(this.waveformData);
      } else {
        this.waveHeights = this.generateNaturalWaveform();
      }
    },
    
    normalizeWaveform(data) {
      if (!data || data.length === 0) return this.generateNaturalWaveform();
      
      const result = [];
      const step = data.length / this.barCount;
      
      for (let i = 0; i < this.barCount; i++) {
        const idx = Math.floor(i * step);
        const val = data[Math.min(idx, data.length - 1)];
        result.push(Math.max(0.1, Math.min(1.0, val)));
      }
      
      return result;
    },
    
    generateNaturalWaveform() {
      const heights = [];
      for (let i = 0; i < this.barCount; i++) {
        const progress = i / this.barCount;
        const envelope = Math.sin(progress * Math.PI) * 0.3 + 0.4;
        const variation = (Math.random() * 0.4) + 0.3;
        heights.push(Math.min(1.0, envelope * variation + 0.2));
      }
      return heights;
    },
    
    drawWaveform() {
      const canvas = this.$refs.waveformCanvas;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      const width = canvas.width;
      const height = canvas.height;
      
      ctx.clearRect(0, 0, width, height);
      
      const barWidth = 3;
      const barGap = 2;
      const totalBarWidth = barWidth + barGap;
      const barsCount = Math.min(this.waveHeights.length, Math.floor(width / totalBarWidth));
      const startX = (width - (barsCount * totalBarWidth)) / 2;
      
      const playedBars = this.duration > 0 
        ? Math.floor((this.currentTime / this.duration) * barsCount)
        : 0;
      
      const playedColor = this.isMe ? '#ffffff' : '#00c444';
      const unplayedColor = this.isMe ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 196, 68, 0.3)';
      
      for (let i = 0; i < barsCount; i++) {
        const barHeight = Math.max(4, this.waveHeights[i] * (height - 8));
        const x = startX + (i * totalBarWidth);
        const y = (height - barHeight) / 2;
        
        ctx.fillStyle = i < playedBars ? playedColor : unplayedColor;
        ctx.beginPath();
        ctx.roundRect(x, y, barWidth, barHeight, 1.5);
        ctx.fill();
      }
    },
    
    async togglePlayPause() {
      if (!this.audio) {
        this.initAudio();
        await this.$nextTick();
      }
      
      if (!this.audio) return;
      
      if (this.isPlaying) {
        this.audio.pause();
      } else {
        try {
          await this.audio.play();
        } catch (e) {
          console.error('[VoiceMessagePlayer] Play error:', e);
        }
      }
    },
    
    async play() {
      if (!this.audio) {
        this.initAudio();
        await this.$nextTick();
      }
      
      if (this.audio && !this.isPlaying) {
        try {
          await this.audio.play();
        } catch (e) {
          console.error('[VoiceMessagePlayer] Auto-play error:', e);
        }
      }
    },
    
    cyclePlaybackSpeed() {
      const currentIndex = this.playbackSpeeds.indexOf(this.playbackSpeed);
      const nextIndex = (currentIndex + 1) % this.playbackSpeeds.length;
      this.playbackSpeed = this.playbackSpeeds[nextIndex];
      
      if (this.audio) {
        this.audio.playbackRate = this.playbackSpeed;
      }
    },
    
    seekToPosition(event) {
      if (!this.audio || !this.duration || !isFinite(this.duration) || this.duration <= 0) return;
      
      const rect = event.currentTarget.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const percent = Math.max(0, Math.min(1, clickX / rect.width));
      const newTime = percent * this.duration;
      
      // Validate newTime is a finite number before setting
      if (!isFinite(newTime) || isNaN(newTime)) return;
      
      this.audio.currentTime = Math.max(0, Math.min(this.duration, newTime));
      this.currentTime = this.audio.currentTime;
      this.drawWaveform();
    },
    
    formatDuration(seconds) {
      if (!seconds || isNaN(seconds)) return '0:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },
    
    onAvatarError(e) {
      e.target.style.display = 'none';
    },
  },
};
</script>

<style scoped>
/* ===== VOICE MESSAGE PLAYER - Modern WhatsApp Style ===== */
.voice-player {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 18px;
  min-width: 240px;
  max-width: 320px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.voice-player--own {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 2px 8px rgba(34, 197, 94, 0.25);
}

/* Avatar */
.voice-player__avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.voice-player__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.voice-player__avatar--placeholder {
  color: #64748b;
  font-size: 18px;
}

/* Play Button - High Contrast */
.voice-player__play-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 18px;
  transition: all 0.2s ease;
  box-shadow: 0 3px 10px rgba(0, 196, 68, 0.35);
}

.voice-player__play-btn i {
  color: #ffffff;
}

.voice-player--own .voice-player__play-btn {
  background: rgba(255, 255, 255, 0.95);
  color: #16a34a;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.voice-player--own .voice-player__play-btn i {
  color: #16a34a;
}

.voice-player__play-btn:hover:not(:disabled) {
  transform: scale(1.08);
  box-shadow: 0 4px 14px rgba(0, 196, 68, 0.45);
}

.voice-player--own .voice-player__play-btn:hover:not(:disabled) {
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.voice-player__play-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.voice-player__play-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.voice-player__loading {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Waveform Container */
.voice-player__waveform-container {
  flex: 1;
  height: 36px;
  position: relative;
  cursor: pointer;
  min-width: 80px;
  border-radius: 4px;
}

.voice-player__waveform {
  width: 100%;
  height: 100%;
  display: block;
}

/* Seek Thumb */
.voice-player__thumb {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  box-shadow: 0 2px 6px rgba(0, 196, 68, 0.4);
  pointer-events: none;
  transition: left 0.05s linear;
}

.voice-player--own .voice-player__thumb {
  background: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

/* Time Display */
.voice-player__time {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  min-width: 38px;
  text-align: center;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}

.voice-player--own .voice-player__time {
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

/* Speed Button */
.voice-player__speed-btn {
  padding: 4px 8px;
  border-radius: 12px;
  border: none;
  background: rgba(0, 0, 0, 0.08);
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
  min-width: 32px;
}

.voice-player--own .voice-player__speed-btn {
  background: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.95);
}

.voice-player__speed-btn:hover {
  background: rgba(0, 0, 0, 0.15);
  transform: scale(1.05);
}

.voice-player--own .voice-player__speed-btn:hover {
  background: rgba(255, 255, 255, 0.35);
}

/* ===== RESPONSIVE ===== */
@media (max-width: 480px) {
  .voice-player {
    min-width: 200px;
    max-width: 280px;
    padding: 8px 12px;
    gap: 8px;
  }
  
  .voice-player__avatar,
  .voice-player__play-btn {
    width: 34px;
    height: 34px;
    font-size: 16px;
  }
  
  .voice-player__waveform-container {
    height: 30px;
  }
  
  .voice-player__thumb {
    width: 10px;
    height: 10px;
  }
  
  .voice-player__time {
    font-size: 11px;
    min-width: 34px;
  }
  
  .voice-player__speed-btn {
    padding: 3px 6px;
    font-size: 10px;
    min-width: 28px;
  }
}

/* ===== DARK MODE SUPPORT ===== */
:root.dark .voice-player,
.dark .voice-player,
[data-theme="dark"] .voice-player {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
}

:root.dark .voice-player__avatar,
.dark .voice-player__avatar,
[data-theme="dark"] .voice-player__avatar {
  background: linear-gradient(135deg, #475569 0%, #64748b 100%);
}

:root.dark .voice-player__avatar--placeholder,
.dark .voice-player__avatar--placeholder,
[data-theme="dark"] .voice-player__avatar--placeholder {
  color: #94a3b8;
}

:root.dark .voice-player__time,
.dark .voice-player__time,
[data-theme="dark"] .voice-player__time {
  color: #cbd5e1;
}

:root.dark .voice-player__speed-btn,
.dark .voice-player__speed-btn,
[data-theme="dark"] .voice-player__speed-btn {
  background: rgba(255, 255, 255, 0.1);
  color: #cbd5e1;
}

:root.dark .voice-player__speed-btn:hover,
.dark .voice-player__speed-btn:hover,
[data-theme="dark"] .voice-player__speed-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}
</style>
